import { FirebaseStorage, connectStorageEmulator, getBlob, getStorage, ref } from 'firebase/storage'
import firebase_app from './firebase-config'

export const FIREBASE_STORAGE_EMULATOR_HOST = '127.0.0.1'
export const FIREBASE_STORAGE_EMULATOR_PORT = 9199

export type GetAppStorageOptions = {
  bucketUrl?: string
  legacy_always_cloud?: boolean
}

const storageInstances = new Map<string, FirebaseStorage>()

/**
 * Reuse the same emulator-vs-cloud decision anywhere behavior needs to match
 * the storage instance being returned for this callsite.
 */
export function usesStorageEmulator(options: GetAppStorageOptions = {}): boolean {
  const { legacy_always_cloud = false } = options
  return !legacy_always_cloud && import.meta.env.MODE === 'development'
}

/** REST v1 media URL for the Storage emulator (SDK `getBlob` uses `/v0/b/` and often 403s locally). */
export function buildStorageEmulatorV1DownloadUrl(bucketId: string, storagePath: string): string {
  const origin = `http://${FIREBASE_STORAGE_EMULATOR_HOST}:${FIREBASE_STORAGE_EMULATOR_PORT}`
  return `${origin}/storage/v1/b/${bucketId}/o/${encodeURIComponent(storagePath)}?alt=media`
}

/** REST v1 media upload URL for the Storage emulator.
 *
 * This is generic storage transport plumbing. user_files authorization still
 * belongs to the user_files API endpoints that choose the object key first.
 */
export function buildStorageEmulatorV1MediaUploadUrl(bucketRef: string, objectKey: string): string {
  const bucketId = bucketRef.replace(/^gs:\/\//, '')
  const origin = `http://${FIREBASE_STORAGE_EMULATOR_HOST}:${FIREBASE_STORAGE_EMULATOR_PORT}`
  return `${origin}/upload/storage/v1/b/${bucketId}/o?uploadType=media&name=${encodeURIComponent(objectKey)}`
}

/**
 * Fetches a Storage object as a Blob. With the local emulator (non-legacy bucket routing), uses the same
 * `/storage/v1/...` URL as user-files download; the JS SDK's `getBlob` targets `/v0/b/...` and fails with 403.
 */
export async function fetchStorageObjectBlob(
  bucketRef: string,
  objectPath: string,
  options: Pick<GetAppStorageOptions, 'legacy_always_cloud'> = {}
): Promise<Blob> {
  const { legacy_always_cloud = false } = options
  if (usesStorageEmulator({ legacy_always_cloud })) {
    const bucketId = bucketRef.replace(/^gs:\/\//, '')
    const url = buildStorageEmulatorV1DownloadUrl(bucketId, objectPath)
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Storage fetch failed with status ${response.status}`)
    }
    return response.blob()
  }
  const storage = getAppStorage({ bucketUrl: bucketRef, legacy_always_cloud })
  return getBlob(ref(storage, objectPath))
}

function getStorageCacheKey(bucketUrl: string | undefined, useEmulator: boolean): string {
  const mode = useEmulator ? 'emulator' : 'cloud'
  return `${mode}:${bucketUrl ?? 'default'}`
}

/**
 * Returns a Firebase storage instance.
 * - default: new stack behavior (uses emulator in local development)
 * - legacy_always_cloud: true forces cloud-backed storage even in local development
 */
export function getAppStorage(options: GetAppStorageOptions = {}): FirebaseStorage {
  const { bucketUrl } = options
  const useEmulator = usesStorageEmulator(options)
  const cacheKey = getStorageCacheKey(bucketUrl, useEmulator)

  const cached = storageInstances.get(cacheKey)
  if (cached) {
    return cached
  }

  const storage = getStorage(firebase_app, bucketUrl)

  if (useEmulator) {
    connectStorageEmulator(storage, FIREBASE_STORAGE_EMULATOR_HOST, FIREBASE_STORAGE_EMULATOR_PORT)
  }

  storageInstances.set(cacheKey, storage)
  return storage
}
